<template>
  <NuxtLayout name="checkout-rooms">
    <h1 class="checkoutUnitList__PageTitle" v-text="activeAccommodation?.name || page.title" />
    <header class="checkoutUnitList__Header">
      <div class="container">
        {{ $t('checkout.additionalRoom') }} ({{ $t('checkout.room') }} {{ numberOfRooms }})
        <nuxt-link :to="{ path: link('Checkout'), query: useRoute().query }" class="checkoutUnitList__CloseButton">
          {{ $t('close') }}
          <Icon name="close" />
        </nuxt-link>
      </div>
    </header>

    <div class="container--Medium">
      <section class="checkoutUnitList__GuestsSelectorWrapper">
        <p v-text="$t('numberOfGuests')"></p>
        <button @click="open('guests')">
          <div>
            {{ $t('adults', numberOfGuests.adults_count) }}<template v-if="numberOfGuests.children">, {{ $t('children', numberOfGuests.children) }}</template>
          </div>

          <Icon name="down" />
        </button>
      </section>
    </div>

    <div class="container--Medium checkoutUnitList__RoomsListContainer" :key="key">
      <section v-if="roomsList?.units" class="checkoutUnitList__AvailableRoomsList">
        <RoomItem @open-modal="handleUnauthorizedUser" v-for="room in roomsList.units" :data="{ ...room, accommodationMealPlans }" :key="room.id" />
      </section>
  
      <section v-if="roomsList?.unavailable_units?.length" class="checkoutUnitList__UnavailableRoomsList">
        <h3 v-text="$t('roomsNotAvailable')" />
        <RoomItem v-for="room in roomsList.unavailable_units" :data="{ ...room, accommodationMealPlans }" :key="room.id" />
      </section>

      <section v-if="roomsList?.units_invalid_capacity?.length" class="checkoutUnitList__UnavailableRoomsList">
        <h3 v-text="$t('roomsOccupantsNotAvailable')" />
        <RoomItem v-for="room in roomsList.units_invalid_capacity" :data="{ ...room, accommodationMealPlans }" :key="room.id" :disable-rates-button="true" />
      </section>
    </div>

    <Modal v-if="dialogs.guests" v-model="dialogs.guests" class="checkoutUnitsList__Modal" @close="closeModal">
      <div class="checkoutUnitList__ModalContent">
        <h2 v-text="$t('numberOfGuests')" />
  
        <div>
          <CounterField
            :label="$t('reservation.adults')"
            v-model="numberOfGuests.adults_count"
            :maxValue="10"
            :minValue="1"
            inline
          ></CounterField>
          <CounterField
            :label="$t('reservation.children')"
            v-model="numberOfGuests.children"
            :maxValue="10"
            :minValue="0"
            inline
          ></CounterField>
          <TextField
            v-for="(child, index) in numberOfGuests.children"
            :label="`${$t('reservation.childAge')} ${index + 1}`"
            v-model="numberOfGuests.children_age[index]"
            :value="numberOfGuests.children_age[index] || 0"
            :validate="{ type: ['number', 'min', 'max'], min: 0, max: 13, message: { max: $t('formErrors.childrenMaxAge') } }"
            min="0"
            type="number"
            grid
          />
        </div>
      </div>

      <div class="checkoutUnitList__ModalButtons">
        <Button medium v-text="$t('confirm')" @click="closeModal" />
      </div>
    </Modal>

    <Loader :active="fetchingData"></Loader>
  </NuxtLayout>
</template>

<script setup>
  import { format, toZonedTime } from 'date-fns-tz';
  defineOptions({
    auth: 'aminess'
  });

  defineProps({
    page: {}
  })
  
  const { link } = useSetup();
  const { booking } = useBooking();
  const { query } = useRoute();
  const { accommodationsData } = useSetup();
  const fetchingData = ref(false);
  let activeAccommodation = ref(query?.accommodation_id ? accommodationsData.value[query.accommodation_id] : null);
  const numberOfGuests = ref({
    adults_count: 2,
    children: 0,
    children_age: [],
  });
  const roomsList = ref([]);
  const key = ref(0);
  const guestsNumberChanged = ref(false);
  let requestBody = {};

  const dialogs = reactive({
    guests: false,
  });
  const { open, close } = useModal(dialogs);

  const accommodationMealPlans = computed(() => {
    if (activeAccommodation.value.accommodation_meal_plans?.length) {
      return activeAccommodation.value.accommodation_meal_plans.reduce((acc, nextItem) => {
        if (acc[nextItem.kind]) return acc;

        acc[nextItem.kind] = {...nextItem}
        return acc;
      }, {})
    }
  });

  async function fetchRoomsData() {
    fetchingData.value = true;
    // every unit from the selected_units array is split by '/' because the value is in the format number of adults/number of children/children ages. For this request we need only roomId
    // selected_units are created in the PricelistSection component
    if (numberOfGuests.value.children_age.length) {
      numberOfGuests.value.children_age = numberOfGuests.value.children_age.map(item => parseInt(item) > 13 ? 13 : parseInt(item));
    }
    const _selectedUnits = booking.value.selected_units && typeof booking.value.selected_units === 'string' ? booking.value.selected_units.split('/')[0] : booking.value.selected_units.map(i => i.split('/')[0])
    requestBody = { ...booking.value, ...numberOfGuests.value, selected_units: _selectedUnits };
    
    requestBody.accommodation = requestBody.accommodation?.id || requestBody.accommodation_id;
    if (requestBody.date_from) requestBody.date_from = format(toZonedTime(new Date(requestBody.date_from), 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' });
    if (requestBody.date_to) requestBody.date_to = format(toZonedTime(new Date(requestBody.date_to), 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' });
    if (requestBody.loyalty_prices) requestBody.loyalty_prices = JSON.parse(requestBody.loyalty_prices);
    
    requestBody.children_age = requestBody.children_age.filter(item => !['', null].includes(item));
    requestBody.children = requestBody.children_age.length;
    numberOfGuests.value.children_age = JSON.parse(JSON.stringify(requestBody.children_age))
    numberOfGuests.value.children = requestBody.children;
    // if there is only one selected unit, the value is a string, so we need to convert it to an array because the API expects an array
    if (requestBody.selected_units && typeof requestBody.selected_units === 'string') {
      requestBody.selected_units = [requestBody.selected_units];
    }
    
    try {
      const response = await useServerFetch('search/unit/', {
        method: 'POST',
        body: requestBody,
      })
      
      if (response) {
        roomsList.value = response;
        activeAccommodation.value = accommodationsData.value[requestBody.accommodation];
        guestsNumberChanged.value = false;
      }
    } catch(error) {
      handleApiErrorsRedirects(error, link('Error'));
    } finally {
      fetchingData.value = false;
      key.value++;
    }
  }

  await fetchRoomsData();

  function closeModal() {
    close('guests');
    if (guestsNumberChanged.value) fetchRoomsData();
  }
  
  // if the auth session expired, the user will be allowd to proceed to the checkout page 
  // on the checkoutForm page we'll try to detect from the email if user is loyalty member otherwise we'll show the password fields and we'll try to register the user
  function handleUnauthorizedUser({ handleProceed }) {
    handleProceed();
  }

  const numberOfRooms = computed(() => {
    return Array.isArray(booking.value.selected_units) ? booking.value.selected_units.length + 1 : 2;
  });

  onMounted(() => {
    if(import.meta.client) {
      if (activeAccommodation.value) getThemeStyles(activeAccommodation.value);
      sessionStorage.setItem('additionalRoomGuests', JSON.stringify(numberOfGuests.value));
    }
  });

  watch(() => numberOfGuests.value, (value) => {
    guestsNumberChanged.value = true;
    if (import.meta.client) sessionStorage.setItem('additionalRoomGuests', JSON.stringify(value));
  }, { deep: true });
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_checkoutUnitList.scss');
</style>